/* eslint-disable max-lines */
import { useSituationLabels } from '@hakimo-ui/hakimo/data-access';
import {
  SiteSchedule,
  Situation,
  SituationColor,
} from '@hakimo-ui/hakimo/types';
import { SelectAsync } from '@hakimo-ui/hakimo/ui-elements';
import { Button, InputField, Modal } from '@hakimo-ui/shared/ui-base';
import { TrashIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useState } from 'react';
import {
  getPayloadFromSchedules,
  validateAllSchedules,
} from '../../schedule/siteSchedule';
import { normalizeSchedules } from '../../sop-workflow/scheduleUtils';
import { ScheduleEditor } from '../../sop-workflow/weekday-actions/ScheduleEditor';
import CameraLabels from '../camera-labels/CameraLabels';
import CameraSelectionModal from '../camera-selection-modal/CameraSelectionModal';
import { SITUATION_COLOR_OPTIONS } from '../constants';

interface CreateUpdateSituationProps {
  onClose: () => void;
  situation: Situation;
  onSubmit: (situation: Situation) => void;
  isNew?: boolean;
  locationId?: string;
}

function CreateUpdateSituationModal(props: CreateUpdateSituationProps) {
  const { onClose, situation, onSubmit, isNew, locationId } = props;
  const [updatedSituation, setUpdatedSituation] = useState<Situation>({
    ...situation,
    subSituations: situation.subSituations ?? [],
    cameras: situation.cameras ?? [],
  });
  const [scheduleGroups, setScheduleGroups] = useState<SiteSchedule[]>(
    normalizeSchedules(situation)
  );
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  const getSituationLabels = useSituationLabels(locationId);

  const onDropdownChange = (value: string) => {
    setUpdatedSituation({ ...updatedSituation, label: value });
  };

  const onChangeColor = (colorId: SituationColor) => () => {
    setUpdatedSituation({ ...updatedSituation, color: colorId });
  };

  const onSubSituationChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const subSituations = [...(updatedSituation.subSituations ?? [])];
      subSituations[index] = e.target.value;
      setUpdatedSituation({ ...updatedSituation, subSituations });
    };

  const onAddSubSituation = () => {
    setUpdatedSituation({
      ...updatedSituation,
      subSituations: [...(updatedSituation.subSituations ?? []), ''],
    });
  };

  const onDeleteSubSituation = (index: number) => () => {
    const subSituations = [...(updatedSituation.subSituations ?? [])];
    subSituations.splice(index, 1);
    setUpdatedSituation({ ...updatedSituation, subSituations });
  };

  const onCameraSelectionSubmit = (selectedCameraNames: string[]) => {
    setUpdatedSituation({ ...updatedSituation, cameras: selectedCameraNames });
  };

  const onSchedulesChange = (schedules: SiteSchedule[]) => {
    setScheduleError(null);
    setScheduleGroups(schedules);
  };

  const validateSchedules = (): SiteSchedule[] | null => {
    const { isValid, message } = validateAllSchedules(scheduleGroups);
    if (!isValid) {
      setScheduleError(message);
      return null;
    }
    setScheduleError(null);
    return scheduleGroups;
  };

  const onSubmitUpdatedValue = () => {
    const validGroups = validateSchedules();
    if (validGroups === null) return;
    onSubmit({
      ...updatedSituation,
      subSituations: (updatedSituation.subSituations ?? []).filter(
        (sub) => sub.trim() !== ''
      ),
      schedules: validGroups.length
        ? getPayloadFromSchedules(
            validGroups,
            situation.schedules?.timezone ?? ''
          )
        : undefined,
    });
  };

  const actions = (
    <>
      <Button
        variant="primary"
        onClick={onSubmitUpdatedValue}
        disabled={updatedSituation.label === ''}
      >
        Submit
      </Button>
      <Button onClick={onClose}>Cancel</Button>
    </>
  );

  return (
    <Modal
      title={isNew ? 'Create new Situation' : 'Update Situation Details'}
      open
      onClose={onClose}
      footer={actions}
    >
      <div className="min-w-[50rem] space-y-4 p-4 px-8">
        <SelectAsync<string>
          value={updatedSituation.label || ''}
          onChange={onDropdownChange}
          displayValue={(item) => item || ''}
          onChangeQuery={getSituationLabels}
          placeholder="Type to search situations..."
          label="Label"
        />

        <div>
          <div className="text-onlight-text-2 dark:text-ondark-text-2 mb-1 block text-sm">
            Color
          </div>
          <div className="flex gap-4">
            {SITUATION_COLOR_OPTIONS.map((color) => (
              <button key={color.id} onClick={onChangeColor(color.id)}>
                <div
                  className={clsx(
                    'flex h-8 w-8 items-center justify-center rounded-full',
                    color.id === SituationColor.RED &&
                      'bg-situation-rose-600 hover:bg-situation-rose-700 dark:bg-situation-rose-400 dark:hover:bg-situation-rose-300',
                    color.id === SituationColor.GREEN &&
                      'bg-situation-teal-600 hover:bg-situation-teal-700 dark:bg-situation-teal-400 dark:hover:bg-situation-teal-300',
                    color.id === SituationColor.BLUE &&
                      'bg-primary-500 hover:bg-primary-700 dark:bg-primary-400 dark:hover:bg-primary-300'
                  )}
                >
                  {color.id === updatedSituation.color && (
                    <div className="h-2 w-2 rounded-full bg-white"></div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-onlight-text-2 dark:text-ondark-text-2 mb-2 block text-sm font-medium">
            Sub-Situations (Optional)
          </div>
          <div className="space-y-2">
            {updatedSituation.subSituations?.map((subSituation, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-2"
              >
                <div className="flex-1">
                  <InputField
                    value={subSituation}
                    type="text"
                    placeholder="Enter sub-situation"
                    onChange={onSubSituationChange(index)}
                  />
                </div>
                <Button variant="icon" onClick={onDeleteSubSituation(index)}>
                  <TrashIcon className="h-5 w-5" />
                </Button>
              </div>
            ))}
            <Button onClick={onAddSubSituation} variant="outline">
              Add Sub-Situation
            </Button>
          </div>
        </div>

        {locationId && (
          <div>
            <div className="text-onlight-text-2 dark:text-ondark-text-2 mb-2 block text-sm">
              Cameras (Optional)
            </div>
            <div className="space-y-2">
              <CameraLabels cameras={updatedSituation.cameras} />
              <Button
                onClick={() => setCameraModalOpen(true)}
                variant="outline"
              >
                Add Cameras
              </Button>
            </div>
          </div>
        )}

        <div>
          <div className="text-onlight-text-2 dark:text-ondark-text-2 mb-2 block text-sm font-medium">
            Schedule (Optional)
          </div>
          <ScheduleEditor
            schedules={scheduleGroups}
            onChange={onSchedulesChange}
            error={scheduleError}
          />
        </div>
      </div>

      {cameraModalOpen && locationId && (
        <CameraSelectionModal
          open={cameraModalOpen}
          onClose={() => setCameraModalOpen(false)}
          onSubmit={onCameraSelectionSubmit}
          locationId={locationId}
          initialSelectedCameras={updatedSituation.cameras || []}
        />
      )}
    </Modal>
  );
}

export default CreateUpdateSituationModal;
